import {
  CSSProperties,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@smartshell/ui-kit';
import { useLocation, useNavigate } from 'react-router-dom';

import { LocalizationContext } from 'localization';
import { ETheme, ThemeContext } from 'theme';

import {
  ONBOARDING_INSERT_SHORTCUT_PATH_EVENT,
  ONBOARDING_PAYMENT_REFUNDED_EVENT,
  ONBOARDING_SESSION_CREATED_EVENT,
  ONBOARDING_SHORTCUT_CREATED_EVENT,
  ONBOARDING_STEP_CHANGED_EVENT,
  OnboardingPaymentRefundedDetail,
  OnboardingSessionCreatedDetail,
  OnboardingShortcutCreatedDetail,
  OnboardingStepChangedDetail,
} from './events';
import { formatOnboardingProgress, getOnboardingCopy } from './localization';
import s from './OnboardingTour.module.scss';
import { OnboardingAction, OnboardingStep } from './steps';
import {
  completeOnboarding,
  isOnboardingCompleted,
  ONBOARDING_RESTART_EVENT,
  resetOnboarding,
} from './storage';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

const PANEL_WIDTH = 420;
const PANEL_HEIGHT = 310;
const GAP = 16;
const SPOTLIGHT_GAP = 6;
const ONBOARDING_MASK_ID = 'sandbox-onboarding-mask';

const AUTO_ADVANCE_ACTIONS = new Set<OnboardingAction>([
  'open-session-sale',
  'select-one-hour-tariff',
  'select-cash-payment',
  'open-session-refund',
  'open-template-library',
  'select-report-review',
  'select-report-sales',
  'select-report-hosts',
]);

const getVisibleElement = (selector: string): HTMLElement | null =>
  Array.from(document.querySelectorAll<HTMLElement>(selector)).find(
    (element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0'
      );
    }
  ) ?? null;

const getTargetElement = (step: OnboardingStep): HTMLElement | null =>
  getVisibleElement(step.target) ??
  document.querySelector<HTMLElement>(step.target);

const toggleUserMenu = (): void => {
  const opener = document.querySelector<HTMLElement>(
    '[data-onboarding="user-menu"]'
  );
  opener?.querySelector<HTMLButtonElement>('button')?.click();
};

const closeUserMenu = (): void => {
  if (document.querySelector('[data-onboarding="theme-switcher"]')) {
    toggleUserMenu();
  }
};

const hasOnboardingFlag = (selector: string, attribute: string): boolean =>
  document.querySelector<HTMLElement>(selector)?.getAttribute(attribute) ===
  'true';

const isActionSatisfied = (
  action: OnboardingAction | undefined,
  theme: ETheme,
  completedActions: ReadonlySet<OnboardingAction>
): boolean => {
  switch (action) {
    case 'switch-theme':
      return theme === ETheme.DARK;
    case 'open-session-sale':
      return Boolean(
        document.querySelector('[data-onboarding="start-session-sale"]')
      );
    case 'select-one-hour-tariff':
      return hasOnboardingFlag(
        '[data-onboarding="start-session-sale"]',
        'data-onboarding-tariff-selected'
      );
    case 'select-cash-payment':
      return hasOnboardingFlag(
        '[data-onboarding="start-session-sale"]',
        'data-onboarding-cash-selected'
      );
    case 'complete-one-hour-session':
    case 'complete-session-refund':
    case 'complete-template-shortcut':
      return completedActions.has(action);
    case 'open-session-refund':
      return Boolean(
        document.querySelector('[data-onboarding="refund-confirm"]')
      );
    case 'open-template-library':
      return Boolean(
        document.querySelector('[data-onboarding="shortcut-template-library"]')
      );
    case 'apply-shortcut-template':
      return hasOnboardingFlag(
        '[data-onboarding="shortcut-form"]',
        'data-onboarding-template-applied'
      );
    case 'enter-shortcut-path':
      return hasOnboardingFlag(
        '[data-onboarding="shortcut-form"]',
        'data-onboarding-path-ready'
      );
    case 'select-report-review':
      return Boolean(
        document.querySelector('[data-onboarding-report-tab="REVIEW"]')
      );
    case 'select-report-sales':
      return Boolean(
        document.querySelector('[data-onboarding-report-tab="SALES"]')
      );
    case 'select-report-hosts':
      return Boolean(
        document.querySelector(
          '[data-onboarding-report-tab="HOSTS_OCCUPATION"]'
        )
      );
    case 'generate-report-review':
      return hasOnboardingFlag(
        '[data-onboarding="report-review-generate"]',
        'data-onboarding-report-generated'
      );
    case 'generate-report-sales':
      return hasOnboardingFlag(
        '[data-onboarding="report-sales-generate"]',
        'data-onboarding-report-generated'
      );
    case 'generate-report-hosts':
      return hasOnboardingFlag(
        '[data-onboarding="report-hosts-generate"]',
        'data-onboarding-report-generated'
      );
    default:
      return true;
  }
};

const toTargetRect = (element: HTMLElement | null): TargetRect | null => {
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    right: rect.right,
    bottom: rect.bottom,
  };
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const getPanelStyle = (
  rect: TargetRect | null,
  step: OnboardingStep,
  panelAnchorRect: TargetRect | null
): CSSProperties => {
  if (panelAnchorRect) {
    if (step.completedPanelPlacement === 'below-center') {
      return {
        left: clamp(
          panelAnchorRect.left + (panelAnchorRect.width - PANEL_WIDTH) / 2,
          GAP,
          window.innerWidth - PANEL_WIDTH - GAP
        ),
        top: clamp(
          panelAnchorRect.bottom + GAP,
          GAP,
          window.innerHeight - PANEL_HEIGHT - GAP
        ),
      };
    }

    return {
      left: clamp(
        panelAnchorRect.right - PANEL_WIDTH - GAP,
        GAP,
        window.innerWidth - PANEL_WIDTH - GAP
      ),
      top: clamp(
        panelAnchorRect.top + GAP,
        GAP,
        window.innerHeight - PANEL_HEIGHT - GAP
      ),
    };
  }

  if (!rect) {
    return {
      left: clamp(
        (window.innerWidth - PANEL_WIDTH) / 2,
        GAP,
        window.innerWidth
      ),
      top: clamp(
        (window.innerHeight - PANEL_HEIGHT) / 2,
        GAP,
        window.innerHeight
      ),
    };
  }

  if (step.placement === 'inside') {
    return {
      left: clamp(rect.right - PANEL_WIDTH - 24, GAP, window.innerWidth),
      top: clamp(rect.top + 24, GAP, window.innerHeight - PANEL_HEIGHT - GAP),
    };
  }

  const desiredLeft =
    step.placement === 'left'
      ? rect.left - PANEL_WIDTH - (step.panelGap ?? GAP)
      : rect.right + GAP;

  return {
    left: clamp(desiredLeft, GAP, window.innerWidth - PANEL_WIDTH - GAP),
    top: clamp(rect.top, GAP, window.innerHeight - PANEL_HEIGHT - GAP),
  };
};

export const OnboardingTour: FC = () => {
  const { lang } = useContext(LocalizationContext);
  const { theme, setTheme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    controls,
    requirements,
    steps: onboardingSteps,
  } = useMemo(() => getOnboardingCopy(lang), [lang]);
  const [isActive, setIsActive] = useState(
    () => !isOnboardingCompleted(window.localStorage)
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [secondaryTargetRect, setSecondaryTargetRect] =
    useState<TargetRect | null>(null);
  const [panelAnchorRect, setPanelAnchorRect] = useState<TargetRect | null>(
    null
  );
  const [completedActions, setCompletedActions] = useState<
    Set<OnboardingAction>
  >(new Set());
  const [sessionHostId, setSessionHostId] = useState<number | null>(null);
  const [sessionPaymentId, setSessionPaymentId] = useState<number | null>(null);
  const [createdShortcutId, setCreatedShortcutId] = useState<number | null>(
    null
  );
  const scrolledRuntimeTargetRef = useRef<string | null>(null);

  const step = onboardingSteps[stepIndex];
  const isLastStep = stepIndex === onboardingSteps.length - 1;
  const canContinue = isActionSatisfied(step.action, theme, completedActions);
  const requirement = canContinue
    ? null
    : step.action
    ? requirements[step.action]
    : null;

  const getCurrentTargetElement = useCallback((): HTMLElement | null => {
    if (
      step.action === 'open-session-sale' &&
      document.querySelector('[data-onboarding="start-session-sale"]')
    ) {
      return null;
    }

    if (
      step.action === 'complete-one-hour-session' &&
      completedActions.has('complete-one-hour-session') &&
      sessionHostId
    ) {
      const marker = document.querySelector<HTMLElement>(
        `[data-onboarding-host-id="${sessionHostId}"]`
      );
      return (
        marker?.closest<HTMLElement>('[role="row"]') ??
        marker?.closest<HTMLElement>('tr') ??
        marker
      );
    }

    if (step.action === 'open-session-refund' && sessionPaymentId) {
      return (
        document.querySelector<HTMLElement>(
          `[data-onboarding-payment-id="${sessionPaymentId}"]`
        ) ?? getTargetElement(step)
      );
    }

    if (
      step.action === 'complete-session-refund' &&
      completedActions.has('complete-session-refund') &&
      sessionPaymentId
    ) {
      const marker = document.querySelector<HTMLElement>(
        `[data-onboarding-payment-id="${sessionPaymentId}"]`
      );
      return (
        marker?.closest<HTMLElement>('[role="row"]') ??
        marker?.closest<HTMLElement>('tr') ??
        marker
      );
    }

    if (step.action === 'complete-template-shortcut' && createdShortcutId) {
      return document.querySelector<HTMLElement>(
        `[data-onboarding-shortcut-id="${createdShortcutId}"]`
      );
    }

    return getTargetElement(step);
  }, [
    completedActions,
    createdShortcutId,
    sessionHostId,
    sessionPaymentId,
    step,
  ]);

  const getSecondaryTargetElement = useCallback((): HTMLElement | null => {
    if (canContinue && step.completedSecondaryTarget) {
      return getVisibleElement(step.completedSecondaryTarget);
    }

    if (!step.secondaryTarget) return null;

    return (
      getVisibleElement('[aria-current="page"]') ??
      getVisibleElement('[data-onboarding-menu-active="true"]')
    );
  }, [canContinue, step.completedSecondaryTarget, step.secondaryTarget]);

  const getPanelAnchorElement = useCallback((): HTMLElement | null => {
    if (
      step.action !== 'complete-one-hour-session' ||
      !completedActions.has('complete-one-hour-session') ||
      !sessionHostId
    ) {
      return null;
    }

    const marker = document.querySelector<HTMLElement>(
      `[data-onboarding-host-id="${sessionHostId}"]`
    );

    return (
      marker?.closest<HTMLElement>('[role="row"]') ??
      marker?.closest<HTMLElement>('tr') ??
      marker
    );
  }, [completedActions, sessionHostId, step.action]);

  const runtimeTargetKey = useMemo(() => {
    if (
      step.action === 'complete-one-hour-session' &&
      completedActions.has('complete-one-hour-session') &&
      sessionHostId
    ) {
      return `host:${sessionHostId}`;
    }
    if (
      step.action === 'complete-session-refund' &&
      completedActions.has('complete-session-refund') &&
      sessionPaymentId
    ) {
      return `payment:${sessionPaymentId}`;
    }
    if (step.action === 'complete-template-shortcut' && createdShortcutId) {
      return `shortcut:${createdShortcutId}`;
    }
    return null;
  }, [
    completedActions,
    createdShortcutId,
    sessionHostId,
    sessionPaymentId,
    step.action,
  ]);

  const updateTarget = useCallback(() => {
    if (!isActive) return;

    const element = getCurrentTargetElement();
    if (
      element &&
      runtimeTargetKey &&
      scrolledRuntimeTargetRef.current !== runtimeTargetKey
    ) {
      scrolledRuntimeTargetRef.current = runtimeTargetKey;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setTargetRect(toTargetRect(element));
    setSecondaryTargetRect(toTargetRect(getSecondaryTargetElement()));
    setPanelAnchorRect(toTargetRect(getPanelAnchorElement()));
  }, [
    getCurrentTargetElement,
    getPanelAnchorElement,
    getSecondaryTargetElement,
    isActive,
    runtimeTargetKey,
  ]);

  useEffect(() => {
    if (!isActive) return;

    updateTarget();
    const observer = new MutationObserver(updateTarget);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: [
        'data-onboarding-tariff-selected',
        'data-onboarding-cash-selected',
        'data-onboarding-template-applied',
        'data-onboarding-path-ready',
        'data-onboarding-report-tab',
        'data-onboarding-report-generated',
      ],
      childList: true,
      subtree: true,
    });
    window.addEventListener('resize', updateTarget);
    document.addEventListener('scroll', updateTarget, true);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateTarget);
      document.removeEventListener('scroll', updateTarget, true);
    };
  }, [isActive, location.pathname, location.search, updateTarget]);

  useEffect(() => {
    if (!isActive || !step.route) return;

    const currentRoute = `${location.pathname}${location.search}`;
    if (currentRoute !== step.route) {
      navigate(step.route);
    }

    document.getElementById('main-content')?.scrollTo({ top: 0 });
  }, [isActive, location.pathname, location.search, navigate, step.route]);

  useEffect(() => {
    if (!isActive) return;

    const frame = window.requestAnimationFrame(() => {
      getCurrentTargetElement()?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      updateTarget();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [getCurrentTargetElement, isActive, step.id, updateTarget]);

  useEffect(() => {
    const detail: OnboardingStepChangedDetail = {
      action: isActive ? step.action : undefined,
      highlightMenu: Boolean(
        isActive && (step.secondaryTarget || step.id === 'main-navigation')
      ),
      stepId: step.id,
    };

    if (detail.action) {
      document.body.dataset.sandboxOnboardingAction = detail.action;
    } else {
      delete document.body.dataset.sandboxOnboardingAction;
    }

    window.dispatchEvent(
      new CustomEvent<OnboardingStepChangedDetail>(
        ONBOARDING_STEP_CHANGED_EVENT,
        { detail }
      )
    );

    return () => {
      if (document.body.dataset.sandboxOnboardingAction === detail.action) {
        delete document.body.dataset.sandboxOnboardingAction;
      }
    };
  }, [isActive, location.pathname, step.action, step.id, step.secondaryTarget]);

  useEffect(() => {
    if (
      !isActive ||
      !canContinue ||
      !step.action ||
      !AUTO_ADVANCE_ACTIONS.has(step.action)
    ) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setStepIndex((current) =>
        Math.min(current + 1, onboardingSteps.length - 1)
      );
    });

    return () => window.cancelAnimationFrame(frame);
  }, [canContinue, isActive, step.action]);

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        completeOnboarding(window.localStorage);
        closeUserMenu();
        setIsActive(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);

  useEffect(() => {
    const completeSession = (event: Event): void => {
      const { hostId, paymentId } = (
        event as CustomEvent<OnboardingSessionCreatedDetail>
      ).detail;
      setSessionHostId(hostId);
      setSessionPaymentId(paymentId ?? null);
      setCompletedActions((current) =>
        new Set(current).add('complete-one-hour-session')
      );
    };
    const completeShortcut = (event: Event): void => {
      const { shortcutId } = (
        event as CustomEvent<OnboardingShortcutCreatedDetail>
      ).detail;
      setCreatedShortcutId(shortcutId);
      setCompletedActions((current) =>
        new Set(current).add('complete-template-shortcut')
      );
    };
    const completeRefund = (event: Event): void => {
      const { paymentId } = (
        event as CustomEvent<OnboardingPaymentRefundedDetail>
      ).detail;
      setSessionPaymentId(paymentId);
      setCompletedActions((current) =>
        new Set(current).add('complete-session-refund')
      );
    };

    window.addEventListener(ONBOARDING_SESSION_CREATED_EVENT, completeSession);
    window.addEventListener(
      ONBOARDING_SHORTCUT_CREATED_EVENT,
      completeShortcut
    );
    window.addEventListener(ONBOARDING_PAYMENT_REFUNDED_EVENT, completeRefund);

    return () => {
      window.removeEventListener(
        ONBOARDING_SESSION_CREATED_EVENT,
        completeSession
      );
      window.removeEventListener(
        ONBOARDING_SHORTCUT_CREATED_EVENT,
        completeShortcut
      );
      window.removeEventListener(
        ONBOARDING_PAYMENT_REFUNDED_EVENT,
        completeRefund
      );
    };
  }, []);

  useEffect(() => {
    const restart = (): void => {
      closeUserMenu();
      resetOnboarding(window.localStorage);
      setTheme(ETheme.LIGHT);
      setStepIndex(0);
      setCompletedActions(new Set());
      setSessionHostId(null);
      setSessionPaymentId(null);
      setCreatedShortcutId(null);
      scrolledRuntimeTargetRef.current = null;
      setIsActive(true);
      navigate('/');
    };

    window.addEventListener(ONBOARDING_RESTART_EVENT, restart);
    return () => window.removeEventListener(ONBOARDING_RESTART_EVENT, restart);
  }, [navigate, setTheme]);

  useEffect(() => {
    if (isActive && stepIndex === 0) {
      setTheme(ETheme.LIGHT);
    }
  }, [isActive, setTheme, stepIndex]);

  const panelStyle = useMemo(
    () => getPanelStyle(targetRect, step, panelAnchorRect),
    [panelAnchorRect, step, targetRect]
  );

  if (!isActive) return null;

  const finish = (): void => {
    completeOnboarding(window.localStorage);
    closeUserMenu();
    setIsActive(false);
    navigate('/');
  };

  const skip = (): void => {
    completeOnboarding(window.localStorage);
    closeUserMenu();
    setIsActive(false);
  };

  const goNext = (): void => {
    if (!canContinue) return;

    if (
      step.action === 'open-user-menu' &&
      !getTargetElement(onboardingSteps[1])
    ) {
      toggleUserMenu();
    }

    if (step.action === 'switch-theme') {
      closeUserMenu();
    }

    if (isLastStep) {
      finish();
      return;
    }

    setStepIndex((current) => current + 1);
  };

  const goBack = (): void => {
    if (stepIndex === 0) return;

    const previousStep = onboardingSteps[stepIndex - 1];
    if (
      previousStep.action === 'switch-theme' &&
      !getTargetElement(previousStep)
    ) {
      toggleUserMenu();
    }
    setStepIndex((current) => current - 1);
  };

  const nextLabel =
    step.action === 'open-user-menu'
      ? controls.openMenu
      : isLastStep
      ? controls.finish
      : controls.next;

  const actionCompleted = Boolean(
    step.action && completedActions.has(step.action)
  );
  const visibleTitle =
    actionCompleted && step.completedTitle ? step.completedTitle : step.title;
  const visibleDescription =
    actionCompleted && step.completedDescription
      ? step.completedDescription
      : step.description;
  const spotlightRects = [targetRect, secondaryTargetRect].filter(
    (rect): rect is TargetRect => Boolean(rect)
  );

  const insertShortcutPath = (): void => {
    window.dispatchEvent(new Event(ONBOARDING_INSERT_SHORTCUT_PATH_EVENT));
  };

  return (
    <div data-testid="sandbox-onboarding">
      {spotlightRects.length > 0 && (
        <svg className={s.overlay} aria-hidden="true">
          <defs>
            <mask id={ONBOARDING_MASK_ID} maskUnits="userSpaceOnUse">
              <rect width="100%" height="100%" fill="white" />
              {spotlightRects.map((rect, index) => (
                <rect
                  key={`mask-${index}`}
                  x={rect.left - SPOTLIGHT_GAP}
                  y={rect.top - SPOTLIGHT_GAP}
                  width={rect.width + SPOTLIGHT_GAP * 2}
                  height={rect.height + SPOTLIGHT_GAP * 2}
                  rx="12"
                  fill="black"
                />
              ))}
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(7, 10, 18, 0.72)"
            mask={`url(#${ONBOARDING_MASK_ID})`}
          />
        </svg>
      )}
      {spotlightRects.map((rect, index) => (
        <div
          key={`spotlight-${index}`}
          className={s.spotlight}
          style={{
            top: rect.top - SPOTLIGHT_GAP,
            left: rect.left - SPOTLIGHT_GAP,
            width: rect.width + SPOTLIGHT_GAP * 2,
            height: rect.height + SPOTLIGHT_GAP * 2,
          }}
        />
      ))}
      <section
        className={s.panel}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
      >
        <div className={s.progress}>
          <span>
            {formatOnboardingProgress(
              controls.progress,
              stepIndex + 1,
              onboardingSteps.length
            )}
          </span>
          <div className={s.progressLine} aria-hidden="true">
            <div
              className={s.progressValue}
              style={{
                width: `${((stepIndex + 1) / onboardingSteps.length) * 100}%`,
              }}
            />
          </div>
        </div>
        <h2 id="onboarding-title" className={s.title}>
          {visibleTitle}
        </h2>
        <p className={s.description}>{visibleDescription}</p>
        {requirement && <div className={s.requirement}>{requirement}</div>}
        {step.action === 'enter-shortcut-path' && !canContinue && (
          <Button
            variant="outline"
            className={s.helper}
            onClick={insertShortcutPath}
          >
            {controls.insertPath}
          </Button>
        )}
        <div className={s.actions}>
          <Button variant="ghost" className={s.skip} onClick={skip}>
            {controls.skip}
          </Button>
          {stepIndex > 0 && (
            <Button variant="ghost" onClick={goBack}>
              {controls.back}
            </Button>
          )}
          <Button disabled={!canContinue} onClick={goNext}>
            {nextLabel}
          </Button>
        </div>
      </section>
    </div>
  );
};
